import * as FC from "@fluentui/react-components";
import * as IC from "@fluentui/react-icons";

import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Context } from "../../context";
import { LabelValue } from "../../components/label_value";
import { toDateString, toDateTimeString } from "../../date_utils";
import {
    formatFloat,
    idString,
    mapLabelBy,
    mapPart,
    mapTaskStatus,
} from "../../db_utils";
import { LeafPartWithDefection } from "./field";
import { SimpleGrid } from "../../components/simple_grid";
import { useSearchParams } from "react-router-dom";
import * as _ from "lodash";
import { parseDateAsPath } from "../../db_utils";

const getFrameDataUrl = async (frameDataId) => {
    try {
        const apiEndpoint = process.env.REACT_APP_API_ENDPOINT || 'http://localhost:5001';
        const response = await fetch(`${apiEndpoint}/api/file/url/framedata/${frameDataId}`, {
            headers: {
                "Authorization": `Bearer ${localStorage.getItem('token')}`
            }
        });
        const data = await response.json();
        return data.url;
    } catch (error) {
        console.error('Error getting frame data URL:', error);
        return null;
    }
};

const listFrames = async (db, { taskId, turbineId }) => {
    let query = `select * from frame_data`;
    let w = [];
    let vars = {};
    if (taskId) {
        w.push(`from_raw.task.id=type::thing($taskId)`);
        vars["taskId"] = taskId;
    }
    if (turbineId) {
        w.push(`from_raw.leaf.turbine.id=type::thing($turbineId)`);
        vars["turbineId"] = turbineId;
        // vars.turbineId = turbineId;
    }
    if (w.length > 0) {
        w = w.join(" AND ");
        query += ` WHERE ${w}`;
    }

    query +=
        " fetch from_raw,from_raw.task, from_raw.task.owner_creater, from_raw.task.owner_checker, from_raw.task.owner_rechecker, from_raw.leaf,from_raw.leaf.model,from_raw.leaf.turbine,from_raw.leaf.turbine.model,from_raw.leaf.turbine.field_item";
    // console.log("query:", query, "vars:", vars);
    const [rets] = await db.query(query, vars);
    return rets;
};

const listDefections = async (db, { taskId, turbineId, defectionId }) => {
    let query = `select * from defection`;
    let w = [];
    let vars = {};
    if (taskId) {
        w.push("from_frame.from_raw.task.id=type::thing($taskId)");
        vars.taskId = taskId;
    }
    if (turbineId) {
        w.push("from_frame.from_raw.leaf.turbine.id=type::thing($turbineId)");
        vars.turbineId = turbineId;
    }
    if (defectionId) {
        w.push("id=type::thing($defectionId)");
        vars.defectionId = defectionId;
    }
    if (w.length > 0) {
        w = w.join(" AND ");
        query += ` WHERE ${w}`;
    }

    query +=
        " fetch label,from_frame,from_frame.from_raw,from_frame.from_raw.task, from_frame.from_raw.task.owner_creater, from_frame.from_raw.task.owner_checker, from_frame.from_raw.task.owner_rechecker, from_frame.from_raw.leaf,from_frame.from_raw.leaf.model,from_frame.from_raw.leaf.turbine,from_frame.from_raw.leaf.turbine.model,from_frame.from_raw.leaf.turbine.field_item";
    const [rets] = await db.query(query, vars);
    return rets;
};

const BaseInfo = ({ frame, defectionGrid }) => {
    const raw = frame?.from_raw;
    const leaf = raw?.leaf;
    const turbine = leaf?.turbine;
    const field_item = turbine?.field_item;
    const task = raw?.task;
    return (
        <div className="flex flex-col">
            <div>
                <h3 className="text-base font-bold my-4">基本信息</h3>
                <div className="grid grid-cols-[auto_1fr] gap-4">
                    <LabelValue label="风机编号" value={turbine?.seq} />
                    <LabelValue label="风机名称" value={turbine?.name} />
                    <LabelValue
                        label="安装日期"
                        value={toDateString(turbine?.installed_at)}
                    />
                    <LabelValue label="风场名称" value={field_item?.name} />
                    <LabelValue label="任务编号" value={task?.seq} />
                    <LabelValue label="任务名称" value={task?.name} />
                    <LabelValue
                        label="创建人员"
                        value={task?.owner_creater?.name}
                    />
                    <LabelValue
                        label="巡检人员"
                        value={task?.owner_checker?.name}
                    />
                    <LabelValue
                        label="巡检时间"
                        value={toDateTimeString(task?.check_at)}
                    />
                    <LabelValue
                        label="复核人员"
                        value={task?.owner_rechecker?.name}
                    />
                    <LabelValue
                        label="复核时间"
                        value={toDateTimeString(task?.recheck_at)}
                    />
                </div>
            </div>
            <div>
                <h3 className="text-base font-bold my-4">图片信息</h3>

                <div className="grid grid-cols-[auto_1fr] gap-4">
                    <LabelValue label="叶片编号" value={leaf?.seq} />
                    <LabelValue
                        label="拍摄时间"
                        value={toDateTimeString(raw?.uploaded_at)}
                    />
                    <LabelValue
                        label="叶片部位"
                        value={mapPart[raw?.part]?.text}
                    />
                    <LabelValue
                        label="所处深度"
                        value={`${formatFloat(frame?.distance)}m`}
                    />
                    <LabelValue
                        label="复核状态"
                        value={mapTaskStatus[task?.status]?.text}
                    />
                </div>
            </div>
            <div>
                <h3 className="text-base font-bold my-4">缺陷记录</h3>
                {defectionGrid}
            </div>
        </div>
    );
};

const preloadImage = (imageSrc) => {
    let img = new Image();
    img.src = imageSrc;
    return img;
};

export const ViewFrames = ({
    taskId,
    turbineId,
    defectionId,
    onlyDefectionFrames = true,
}) => {
    const { db, oss } = useContext(Context);

    const [defections, setDefections] = useState([]);
    const [frames, setFrames] = useState([]);
    const [currentFrameIndex, setCurrentFrameIndex] = useState(0);
    const [imageCache, setImageCache] = useState([]);
    const [currentFrameUrl, setCurrentFrameUrl] = useState(null);

    const loadData = async () => {
        if (!defectionId) {
            if (!onlyDefectionFrames) {
                let a = listFrames(db, { taskId, turbineId });
                let b = listDefections(db, { taskId, turbineId });
                let [frames, defections] = await Promise.all([a, b]);

                setFrames(frames);
                setDefections(defections);
            } else {
                let defections = await listDefections(db, {
                    taskId,
                    turbineId,
                });
                let frames = defections
                    .map((defection) => defection?.from_frame)
                    .filter((frame) => frame);
                frames = _.uniqBy(frames, (frame) => idString(frame.id));
                setFrames(frames);
                setDefections(defections);
            }
        } else {
            let defections = await listDefections(db, {
                defectionId: defectionId,
            });
            setDefections(defections);
            let frame = defections?.[0].from_frame;
            let frames = (frame && [frame]) || [];
            setFrames(frames);
        }
        // 这里没有预加载，因为很奇怪的得到frames.length=0
        // TODO: Update preloadFrames to use new getFrameDataUrl API
        // setTimeout(() => preloadFrames(oss), 1000);
    };

    const preloadFrames = (oss) => {
        // console.log("preloadFrames:", currentFrameIndex, frames.length);
        // let imageCache = [];
        // for (let i = 0; i < 5; i++) {
        //     let frameIndex = currentFrameIndex + i + 1;
        //     if (frameIndex >= frames.length) {
        //         break;
        //     }
        //     let frame = frames[frameIndex];
        //     if (!frame) {
        //         break;
        //     }
        //     let url = frameUrl(frame, oss);
        //     let img = preloadImage(url);
        //     imageCache.push(img);
        // }
        // setImageCache(imageCache);
    };

    const currentFrame = useMemo(() => {
        return frames[currentFrameIndex];
    }, [frames, currentFrameIndex]);

    const currentDefections = useMemo(() => {
        return defections.filter(
            (defection) => defection.from_frame?.id == currentFrame?.id,
        );
    }, [frames, currentFrameIndex, defections]);

    useEffect(() => {
        if (!db || !oss) {
            return;
        }
        loadData();
    }, [db, oss]);

    // Load frame URL when current frame changes
    useEffect(() => {
        const loadFrameUrl = async () => {
            if (currentFrame?.id) {
                const url = await getFrameDataUrl(currentFrame.id);
                setCurrentFrameUrl(url);
            } else {
                setCurrentFrameUrl(null);
            }
        };
        loadFrameUrl();
    }, [currentFrame?.id]);

    const defectionColumns = [
        FC.createTableColumn({
            columnId: "seq",
            renderHeaderCell: () => {
                return <FC.TableCellLayout>缺陷编号</FC.TableCellLayout>;
            },
            renderCell: (item) => {
                return (
                    <FC.TableCellLayout>{item.label?.seq}</FC.TableCellLayout>
                );
            },
        }),
        FC.createTableColumn({
            columnId: "name",
            renderHeaderCell: () => {
                return <FC.TableCellLayout>缺陷类型</FC.TableCellLayout>;
            },
            renderCell: (item) => {
                return (
                    <FC.TableCellLayout>{item.label?.name}</FC.TableCellLayout>
                );
            },
        }),
        FC.createTableColumn({
            columnId: "label_by",
            renderHeaderCell: () => {
                return <FC.TableCellLayout>方式</FC.TableCellLayout>;
            },
            renderCell: (item) => {
                return (
                    <FC.TableCellLayout>
                        {mapLabelBy[item.label_by]?.text}
                    </FC.TableCellLayout>
                );
            },
        }),
    ];
    const saturateFrameIndex = (v) => {
        if (v <= 0) {
            v = 0;
        }
        if (v >= frames.length) {
            v = frames.length - 1;
        }
        return v;
    };
    const changeCurrentFrame = (frameIndex) => {
        frameIndex = saturateFrameIndex(frameIndex);
        setCurrentFrameIndex(frameIndex);
        setTimeout(() => {
            preloadFrames(oss);
        }, 100);
    };

    const onPrev = () => {
        let v = currentFrameIndex - 1;
        changeCurrentFrame(v);
    };
    const onNext = () => {
        let v = currentFrameIndex + 1;
        changeCurrentFrame(v);
    };
    const onChangeCurrentFrameIndex = (v) => {
        v = parseInt(v);
        changeCurrentFrame(v - 1);
    };
    const defectionGrid = (
        <SimpleGrid
            items={currentDefections || []}
            columns={defectionColumns}
        />
    );
    return (
        <div className="flex flex-row gap-12 flex-1">
            <div className="flex flex-col">
                <div className="flex flex-row gap-2 items-center">
                    {(frames.length > 1 && (
                        <>
                            当前图片：
                            <FC.Button
                                icon={<IC.ArrowCircleLeftRegular />}
                                onClick={onPrev}
                                appearance="subtle"
                            />
                            <FC.Input
                                type="number"
                                className="w-20"
                                value={currentFrameIndex + 1}
                                onChange={(evt, data) => {
                                    onChangeCurrentFrameIndex(data.value);
                                }}
                            />
                            /{frames.length}
                            <FC.Button
                                icon={<IC.ArrowCircleRightRegular />}
                                appearance="subtle"
                                onClick={onNext}
                            />
                        </>
                    )) || <div className="">&nbsp;</div>}
                    {/*                    <div className="flex-1"></div>
                    <FC.Button
                        icon={<IC.AddCircleRegular />}
                        appearance="subtle"
                    />
                    <FC.Button
                        icon={<IC.SubtractCircleRegular />}
                        appearance="subtle"
                    />
                    <FC.Button
                        icon={<IC.ArrowDownloadRegular />}
                        appearance="subtle"
                    />
*/}
                </div>
                {currentFrame && (
                    <div className="w-fit h-fit relative">
                        {currentFrameUrl ? (
                            <img
                                src={currentFrameUrl}
                                className="object-contain"
                            />
                        ) : (
                            <div className="w-full h-64 flex items-center justify-center bg-gray-100">
                                Loading image...
                            </div>
                        )}
                        {currentDefections.map((defection) => {
                            return (
                                <div
                                    className="absolute border box-border bg-[#ee333322]"
                                    key={defection?.id}
                                    style={{
                                        left: `${defection.x * 100}%`,
                                        top: `${defection.y * 100}%`,
                                        width: `${defection.width * 100}%`,
                                        height: `${defection.height * 100}%`,
                                        borderColor: `${
                                            defection.label?.color || "red"
                                        }`,
                                    }}
                                ></div>
                            );
                        })}
                    </div>
                )}
            </div>
            <div>
                <h3 className="text-base font-bold my-4 whitespace-nowrap">
                    图像位置
                </h3>
                {currentFrame?.from_raw?.leaf &&
                    currentFrame?.from_raw?.task && (
                        <LeafPartWithDefection
                            leaf={currentFrame?.from_raw?.leaf}
                            part={currentFrame?.from_raw?.part}
                            taskId={currentFrame?.from_raw?.task?.id}
                            defections={currentDefections}
                        />
                    )}
            </div>
            {currentFrame?.from_raw?.leaf?.turbine && (
                <BaseInfo frame={currentFrame} defectionGrid={defectionGrid} />
            )}
        </div>
    );
};

export const DatacenterViewFrames = ({}) => {
    const [params, setParams] = useSearchParams();
    console.log("params:", params);
    const defectionId = params.get("defectionId");
    const taskId = params.get("taskId");
    const turbineId = params.get("turbineId");
    return (
        <div className="flex max-h-[80vh]">
            <ViewFrames
                taskId={taskId}
                turbineId={turbineId}
                defectionId={defectionId}
            />
        </div>
    );
};
